import { pgTable, text, boolean, timestamp, integer, index, foreignKey, jsonb, uniqueIndex, serial } from 'drizzle-orm/pg-core';
import { sql } from 'drizzle-orm';
import { user } from './user';
import { adminUser } from './admin';
import { tag } from './tags';

// ---------------------------------------------------------------------------
// Lead Funnel and Status Config (amoCRM-like pipeline -> stages)
// ---------------------------------------------------------------------------

export const leadFunnel = pgTable('LeadFunnel', {
  id: serial().primaryKey().notNull(),
  name: text().notNull(),
  description: text(),
  position: integer().notNull().default(0),
  isDefault: boolean().default(false).notNull(),
  isActive: boolean().default(true).notNull(),
  createdAt: timestamp({ precision: 3, mode: 'date' }).default(sql`CURRENT_TIMESTAMP`).notNull(),
  updatedAt: timestamp({ precision: 3, mode: 'date' }).notNull().$onUpdate(() => new Date()),
}, (table) => [
  index('LeadFunnel_position_idx').using('btree', table.position.asc().nullsLast()),
  index('LeadFunnel_isDefault_idx').using('btree', table.isDefault.asc().nullsLast()),
  index('LeadFunnel_isActive_idx').using('btree', table.isActive.asc().nullsLast()),
]);

export const leadStatusConfig = pgTable('LeadStatusConfig', {
  id: serial().primaryKey().notNull(),
  leadFunnelId: integer().notNull(),
  name: text().notNull(),
  description: text(),
  color: text().default('#6b7280'),
  position: integer().notNull().default(0),
  isSystem: boolean().default(false),
  isInitial: boolean().default(false),
  isFinal: boolean().default(false),
  onEnterActions: jsonb().default([]),
  onExitActions: jsonb().default([]),
  createdAt: timestamp({ precision: 3, mode: 'date' }).default(sql`CURRENT_TIMESTAMP`).notNull(),
  updatedAt: timestamp({ precision: 3, mode: 'date' }).notNull().$onUpdate(() => new Date()),
}, (table) => [
  index('LeadStatusConfig_leadFunnelId_idx').using('btree', table.leadFunnelId.asc().nullsLast()),
  index('LeadStatusConfig_position_idx').using('btree', table.position.asc().nullsLast()),
  index('LeadStatusConfig_isSystem_idx').using('btree', table.isSystem.asc().nullsLast()),
  index('LeadStatusConfig_isFinal_idx').using('btree', table.isFinal.asc().nullsLast()),
  foreignKey({
    columns: [table.leadFunnelId],
    foreignColumns: [leadFunnel.id],
    name: 'LeadStatusConfig_leadFunnelId_fkey',
  }).onUpdate('cascade').onDelete('restrict'),
]);

// ---------------------------------------------------------------------------
// Lead Status Transition (analogue of OrderStatusTransition)
// ---------------------------------------------------------------------------

export const leadStatusTransition = pgTable('LeadStatusTransition', {
  id: serial().primaryKey().notNull(),
  fromStatusId: integer(), // nullable — means "from any status"
  toStatusId: integer().notNull(),
  requireNote: boolean().default(false),
  requireConfirmation: boolean().default(false),
  createdAt: timestamp({ precision: 3, mode: 'date' }).default(sql`CURRENT_TIMESTAMP`).notNull(),
}, (table) => [
  uniqueIndex('LeadStatusTransition_fromTo_key').using('btree', table.fromStatusId.asc().nullsLast(), table.toStatusId.asc().nullsLast()),
  index('LeadStatusTransition_fromStatusId_idx').using('btree', table.fromStatusId.asc().nullsLast()),
  index('LeadStatusTransition_toStatusId_idx').using('btree', table.toStatusId.asc().nullsLast()),
  foreignKey({
    columns: [table.fromStatusId],
    foreignColumns: [leadStatusConfig.id],
    name: 'LeadStatusTransition_fromStatusId_fkey',
  }).onUpdate('cascade').onDelete('cascade'),
  foreignKey({
    columns: [table.toStatusId],
    foreignColumns: [leadStatusConfig.id],
    name: 'LeadStatusTransition_toStatusId_fkey',
  }).onUpdate('cascade').onDelete('cascade'),
]);

// ---------------------------------------------------------------------------
// Lead Webhook — external webhook source configuration
// ---------------------------------------------------------------------------

export const leadWebhook = pgTable('LeadWebhook', {
  id: integer().primaryKey().generatedAlwaysAsIdentity(),
  name: text().notNull(),
  // bcrypt hash of the token — never store the raw token
  tokenHash: text().notNull(),
  fieldMapping: jsonb().default({}).notNull(),
  defaultTagIds: jsonb().default([]).notNull(),
  isActive: boolean().default(true).notNull(),
  deletedAt: timestamp({ precision: 3, mode: 'date' }),
  createdAt: timestamp({ precision: 3, mode: 'date' }).default(sql`CURRENT_TIMESTAMP`).notNull(),
  updatedAt: timestamp({ precision: 3, mode: 'date' }).notNull().$onUpdate(() => new Date()),
}, (table) => [
  index('LeadWebhook_isActive_idx').using('btree', table.isActive.asc().nullsLast()),
  index('LeadWebhook_deletedAt_idx').using('btree', table.deletedAt.desc().nullsLast()),
]);

// ---------------------------------------------------------------------------
// Lead — core entity
// ---------------------------------------------------------------------------

export const lead = pgTable('Lead', {
  id: integer().primaryKey().generatedAlwaysAsIdentity(),
  leadWebhookId: integer(), // nullable FK -> leadWebhook
  userId: integer(), // nullable FK -> user (if matched)
  statusConfigId: integer().notNull(), // FK -> leadStatusConfig
  assignedAdminId: integer(), // nullable FK -> adminUser
  email: text(),
  phone: text(),
  telegramUsername: text(),
  firstName: text(),
  lastName: text(),
  // External idempotency key — unique per webhook source
  externalId: text(),
  // Raw incoming webhook/form data
  metadata: jsonb(),
  // Parsed/structured custom field values
  customData: jsonb(),
  // Link to the registered user after conversion
  convertedUserId: integer(), // nullable FK -> user
  convertedAt: timestamp({ precision: 3, mode: 'date' }),
  deletedAt: timestamp({ precision: 3, mode: 'date' }),
  createdAt: timestamp({ precision: 3, mode: 'date' }).default(sql`CURRENT_TIMESTAMP`).notNull(),
  updatedAt: timestamp({ precision: 3, mode: 'date' }).notNull().$onUpdate(() => new Date()),
}, (table) => [
  index('Lead_userId_idx').using('btree', table.userId.asc().nullsLast()),
  index('Lead_statusConfigId_idx').using('btree', table.statusConfigId.asc().nullsLast()),
  index('Lead_deletedAt_idx').using('btree', table.deletedAt.desc().nullsLast()),
  index('Lead_email_idx').using('btree', table.email.asc().nullsLast()),
  index('Lead_phone_idx').using('btree', table.phone.asc().nullsLast()),
  index('Lead_assignedAdminId_idx').using('btree', table.assignedAdminId.asc().nullsLast()),
  // Idempotency: (webhook, externalId) pair must be unique when both are set
  uniqueIndex('Lead_leadWebhookId_externalId_key')
    .using('btree', table.leadWebhookId.asc().nullsLast(), table.externalId.asc().nullsLast())
    .where(sql`"leadWebhookId" IS NOT NULL AND "externalId" IS NOT NULL`),
  foreignKey({
    columns: [table.leadWebhookId],
    foreignColumns: [leadWebhook.id],
    name: 'Lead_leadWebhookId_fkey',
  }).onUpdate('cascade').onDelete('set null'),
  foreignKey({
    columns: [table.userId],
    foreignColumns: [user.id],
    name: 'Lead_userId_fkey',
  }).onUpdate('cascade').onDelete('set null'),
  foreignKey({
    columns: [table.statusConfigId],
    foreignColumns: [leadStatusConfig.id],
    name: 'Lead_statusConfigId_fkey',
  }).onUpdate('cascade').onDelete('restrict'),
  foreignKey({
    columns: [table.assignedAdminId],
    foreignColumns: [adminUser.id],
    name: 'Lead_assignedAdminId_fkey',
  }).onUpdate('cascade').onDelete('set null'),
  foreignKey({
    columns: [table.convertedUserId],
    foreignColumns: [user.id],
    name: 'Lead_convertedUserId_fkey',
  }).onUpdate('cascade').onDelete('set null'),
]);

// ---------------------------------------------------------------------------
// Lead Tag (analogue of UserTag)
// ---------------------------------------------------------------------------

export const leadTag = pgTable('LeadTag', {
  id: integer().primaryKey().generatedAlwaysAsIdentity(),
  leadId: integer().notNull(),
  tagId: integer().notNull(),
  // source: 'manual' | 'utm_auto' | 'webhook'
  source: text().notNull().default('manual'),
  assignedBy: integer(), // nullable FK -> adminUser (null = auto/system/webhook)
  assignedAt: timestamp({ precision: 3, mode: 'date' }).default(sql`CURRENT_TIMESTAMP`).notNull(),
}, (table) => [
  uniqueIndex('LeadTag_leadId_tagId_key').using('btree', table.leadId.asc(), table.tagId.asc()),
  index('LeadTag_leadId_idx').using('btree', table.leadId.asc().nullsLast()),
  index('LeadTag_tagId_idx').using('btree', table.tagId.asc().nullsLast()),
  foreignKey({
    columns: [table.leadId],
    foreignColumns: [lead.id],
    name: 'LeadTag_leadId_fkey',
  }).onUpdate('cascade').onDelete('cascade'),
  foreignKey({
    columns: [table.tagId],
    foreignColumns: [tag.id],
    name: 'LeadTag_tagId_fkey',
  }).onUpdate('cascade').onDelete('cascade'),
  foreignKey({
    columns: [table.assignedBy],
    foreignColumns: [adminUser.id],
    name: 'LeadTag_assignedBy_fkey',
  }).onUpdate('cascade').onDelete('set null'),
]);

// ---------------------------------------------------------------------------
// Lead History (analogue of OrderHistory)
// ---------------------------------------------------------------------------

export const leadHistory = pgTable('LeadHistory', {
  id: serial().primaryKey().notNull(),
  leadId: integer().notNull(),
  adminUserId: integer(), // nullable — null when actorType is 'system'/'webhook'/'auto'
  // actorType: 'admin' | 'system' | 'webhook' | 'auto'
  actorType: text().notNull(),
  // action: 'created' | 'status_changed' | 'assigned' | 'tag_added' | 'tag_removed' | 'field_updated' | 'converted_to_user'
  action: text().notNull(),
  fromStatusId: integer(),
  toStatusId: integer(),
  previousValue: jsonb(),
  newValue: jsonb(),
  createdAt: timestamp({ precision: 3, mode: 'date' }).default(sql`CURRENT_TIMESTAMP`).notNull(),
}, (table) => [
  index('LeadHistory_leadId_idx').using('btree', table.leadId.asc().nullsLast()),
  index('LeadHistory_adminUserId_idx').using('btree', table.adminUserId.asc().nullsLast()),
  index('LeadHistory_createdAt_idx').using('btree', table.createdAt.desc().nullsLast()),
  index('LeadHistory_action_idx').using('btree', table.action.asc().nullsLast()),
  foreignKey({
    columns: [table.leadId],
    foreignColumns: [lead.id],
    name: 'LeadHistory_leadId_fkey',
  }).onUpdate('cascade').onDelete('cascade'),
  foreignKey({
    columns: [table.adminUserId],
    foreignColumns: [adminUser.id],
    name: 'LeadHistory_adminUserId_fkey',
  }).onUpdate('cascade').onDelete('set null'),
  foreignKey({
    columns: [table.fromStatusId],
    foreignColumns: [leadStatusConfig.id],
    name: 'LeadHistory_fromStatusId_fkey',
  }).onUpdate('cascade').onDelete('set null'),
  foreignKey({
    columns: [table.toStatusId],
    foreignColumns: [leadStatusConfig.id],
    name: 'LeadHistory_toStatusId_fkey',
  }).onUpdate('cascade').onDelete('set null'),
]);

// ---------------------------------------------------------------------------
// Lead Note (analogue of OrderNote)
// ---------------------------------------------------------------------------

export const leadNote = pgTable('LeadNote', {
  id: serial().primaryKey().notNull(),
  leadId: integer().notNull(),
  adminUserId: integer().notNull(),
  content: text().notNull(),
  createdAt: timestamp({ precision: 3, mode: 'date' }).default(sql`CURRENT_TIMESTAMP`).notNull(),
  updatedAt: timestamp({ precision: 3, mode: 'date' }).notNull().$onUpdate(() => new Date()),
}, (table) => [
  index('LeadNote_leadId_idx').using('btree', table.leadId.asc().nullsLast()),
  index('LeadNote_adminUserId_idx').using('btree', table.adminUserId.asc().nullsLast()),
  foreignKey({
    columns: [table.leadId],
    foreignColumns: [lead.id],
    name: 'LeadNote_leadId_fkey',
  }).onUpdate('cascade').onDelete('cascade'),
  foreignKey({
    columns: [table.adminUserId],
    foreignColumns: [adminUser.id],
    name: 'LeadNote_adminUserId_fkey',
  }).onUpdate('cascade').onDelete('cascade'),
]);

// ---------------------------------------------------------------------------
// Lead Custom Field — per-webhook schema definition
// ---------------------------------------------------------------------------

export const leadCustomField = pgTable('LeadCustomField', {
  id: integer().primaryKey().generatedAlwaysAsIdentity(),
  leadWebhookId: integer().notNull(),
  // Machine name, e.g. 'company'
  name: text().notNull(),
  // Display label, e.g. 'Company Name'
  label: text().notNull(),
  // fieldType: 'text' | 'number' | 'date' | 'boolean'
  fieldType: text().notNull(),
  isRequired: boolean().default(false).notNull(),
  position: integer().default(0).notNull(),
  createdAt: timestamp({ precision: 3, mode: 'date' }).default(sql`CURRENT_TIMESTAMP`).notNull(),
  updatedAt: timestamp({ precision: 3, mode: 'date' }).notNull().$onUpdate(() => new Date()),
}, (table) => [
  uniqueIndex('LeadCustomField_leadWebhookId_name_key').using('btree', table.leadWebhookId.asc(), table.name.asc()),
  index('LeadCustomField_leadWebhookId_idx').using('btree', table.leadWebhookId.asc().nullsLast()),
  index('LeadCustomField_position_idx').using('btree', table.position.asc().nullsLast()),
  foreignKey({
    columns: [table.leadWebhookId],
    foreignColumns: [leadWebhook.id],
    name: 'LeadCustomField_leadWebhookId_fkey',
  }).onUpdate('cascade').onDelete('cascade'),
]);
